import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useContractWrite, usePrepareContractWrite, useContractRead, useAccount, useNetwork } from 'wagmi';
import { CONTRACT_ADDRESSES } from '@/config/addresses';
import { factoryABI } from '@/config/contracts';
import { useToast } from '@/components/ui/use-toast';
import { parseEther, formatEther, isAddress } from 'viem';
import { useAdmin } from '@/hooks/useAdmin';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle, ExternalLink } from 'lucide-react';
import { ethers } from 'ethers';

export function ContractManagement() {
  const { toast } = useToast();
  const { address } = useAccount();
  const { chain } = useNetwork();
  const { isAdmin } = useAdmin();
  const [newFee, setNewFee] = useState('');
  const [newFeeReceiver, setNewFeeReceiver] = useState('');
  const [newTemplateAddress, setNewTemplateAddress] = useState('');
  const [newOwnerAddress, setNewOwnerAddress] = useState('');
  const [isOwnerVerified, setIsOwnerVerified] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const chainId = chain?.id || 56; // Default to BSC mainnet if chain is not available
  const factoryAddress = CONTRACT_ADDRESSES[chainId as keyof typeof CONTRACT_ADDRESSES]?.factory;

  // Override factory address specifically for BSC mainnet to ensure it works
  const finalFactoryAddress = chainId === 56 
    ? '0x13cFaDE4cA9cE6a70464408B374a5e576206ea0e' 
    : factoryAddress;

  console.log('Chain ID:', chainId);
  console.log('Factory address:', finalFactoryAddress);

  // Get current platform fee
  const { data: currentFee, isError: feeError, refetch: refetchFee } = useContractRead({
    address: finalFactoryAddress as `0x${string}`,
    abi: factoryABI,
    functionName: 'platformFee',
    watch: true,
    cacheTime: 0,
    enabled: Boolean(finalFactoryAddress),
    onError: (error) => {
      console.error('Error fetching platform fee:', error);
    }
  });

  // Get current fee receiver
  const { data: currentFeeReceiver, isError: receiverError, refetch: refetchReceiver } = useContractRead({
    address: finalFactoryAddress as `0x${string}`,
    abi: factoryABI,
    functionName: 'platformFeeReceiver',
    watch: true,
    cacheTime: 0,
    enabled: Boolean(finalFactoryAddress),
    onError: (error) => {
      console.error('Error fetching fee receiver:', error);
    }
  });

  // Get contract owner
  const { data: contractOwner, isSuccess: ownerFetchSuccess } = useContractRead({
    address: finalFactoryAddress as `0x${string}`,
    abi: factoryABI,
    functionName: 'owner',
    watch: true,
    enabled: Boolean(finalFactoryAddress),
    onError: (error) => {
      console.error('Error fetching owner:', error);
    }
  });

  // Get current template address
  const { data: currentTemplate, refetch: refetchTemplate } = useContractRead({
    address: finalFactoryAddress as `0x${string}`,
    abi: factoryABI,
    functionName: 'templateAddress',
    watch: true,
    cacheTime: 0,
    enabled: Boolean(finalFactoryAddress),
    onError: (error) => {
      console.error('Error fetching template:', error);
    }
  });

  // Log contract read results
  useEffect(() => {
    console.log('Contract read results:', {
      currentFee,
      currentFeeReceiver,
      contractOwner,
      currentTemplate,
      feeError,
      receiverError
    });
  }, [currentFee, currentFeeReceiver, contractOwner, currentTemplate, feeError, receiverError]);

  // Prepare set platform fee
  const { config: setPlatformFeeConfig, error: platformFeeError } = usePrepareContractWrite({
    address: finalFactoryAddress as `0x${string}`,
    abi: factoryABI,
    functionName: 'setPlatformFee',
    args: [newFee ? parseEther(newFee) : 0n],
    enabled: Boolean(finalFactoryAddress && newFee),
  });

  // Prepare set fee receiver
  const { config: setFeeReceiverConfig, error: feeReceiverError } = usePrepareContractWrite({
    address: finalFactoryAddress as `0x${string}`,
    abi: factoryABI,
    functionName: 'setPlatformFeeReceiver',
    args: [newFeeReceiver as `0x${string}`],
    enabled: Boolean(finalFactoryAddress && newFeeReceiver && isOwnerVerified && isAddress(newFeeReceiver)),
  });

  // Prepare set template
  const { config: setTemplateConfig, error: templateError } = usePrepareContractWrite({
    address: finalFactoryAddress as `0x${string}`,
    abi: factoryABI,
    functionName: 'setTemplateAddress',
    args: [newTemplateAddress as `0x${string}`],
    enabled: Boolean(finalFactoryAddress && newTemplateAddress && isOwnerVerified),
  });

  // Prepare transfer ownership
  const { config: transferOwnershipConfig, error: transferOwnershipError } = usePrepareContractWrite({
    address: finalFactoryAddress as `0x${string}`,
    abi: factoryABI,
    functionName: 'transferOwnership',
    args: [newOwnerAddress as `0x${string}`],
    enabled: Boolean(finalFactoryAddress && newOwnerAddress && isOwnerVerified),
  });

  // Write functions
  const { write: writePlatformFee, isLoading: isSettingFee } = useContractWrite({
    ...setPlatformFeeConfig,
    onSuccess: async () => {
      toast({
        title: "Success",
        description: "Platform fee updated successfully",
      });
      setNewFee('');
      await refetchFee();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const { write: writeFeeReceiver, isLoading: isSettingReceiver } = useContractWrite({
    ...setFeeReceiverConfig,
    onSuccess: async () => {
      toast({
        title: "Success",
        description: "Fee receiver updated successfully",
      });
      setNewFeeReceiver('');
      await refetchReceiver();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const { write: writeTemplate, isLoading: isSettingTemplate } = useContractWrite({
    ...setTemplateConfig,
    onSuccess: async () => {
      toast({
        title: "Success",
        description: "Template updated successfully",
      });
      setNewTemplateAddress('');
      await refetchTemplate();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const { write: writeTransferOwnership, isLoading: isTransferringOwnership } = useContractWrite({
    ...transferOwnershipConfig,
    onSuccess: async () => {
      toast({
        title: "Success",
        description: "Ownership transfer initiated successfully",
      });
      setNewOwnerAddress('');
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Verify owner on component mount
  useEffect(() => {
    if (ownerFetchSuccess && contractOwner && address) {
      const isOwner = contractOwner.toLowerCase() === address.toLowerCase();
      setIsOwnerVerified(isOwner);
    }
  }, [contractOwner, address, ownerFetchSuccess]);

  // Validate fee input
  const validateFee = useCallback((value: string) => {
    if (!value) {
      setValidationError('Fee is required');
      return false;
    }
    if (isNaN(Number(value)) || Number(value) < 0) {
      setValidationError('Fee must be a positive number');
      return false;
    }
    setValidationError(null);
    return true;
  }, []);

  // Get native currency symbol based on chain
  const getNativeCurrency = useCallback(() => {
    if (chain?.id === 56 || chain?.id === 97) return 'BNB';
    if (chain?.id === 204 || chain?.id === 5611) return 'BNB'; // opBNB
    if (chain?.id === 1 || chain?.id === 11155111) return 'ETH';
    if (chain?.id === 43114 || chain?.id === 43113) return 'AVAX';
    if (chain?.id === 137 || chain?.id === 80002) return 'MATIC'; // Polygon Mainnet & Amoy
    if (chain?.id === 8453 || chain?.id === 84532) return 'ETH'; // Base
    if (chain?.id === 10 || chain?.id === 11155420) return 'ETH'; // Optimism
    if (chain?.id === 42161 || chain?.id === 421614) return 'ETH'; // Arbitrum
    if (chain?.id === 11124 || chain?.id === 2741) return 'ETH'; // Abstract
    if (chain?.id === 1328 || chain?.id === 1329) return 'SEI'; // Sei Atlantic-2 & Pacific-1
    return 'ETH'; // Default to ETH
  }, [chain?.id]);

  // Handle fee update
  const handleFeeUpdate = useCallback(() => {
    if (!validateFee(newFee)) return;
    if (!writePlatformFee) {
      toast({
        title: "Error",
        description: platformFeeError?.message || "Unable to update platform fee",
        variant: "destructive",
      });
      return;
    }
    writePlatformFee();
  }, [newFee, validateFee, writePlatformFee, platformFeeError, toast]);

  // Handle receiver update
  const handleReceiverUpdate = useCallback(() => {
    if (!newFeeReceiver) {
      setValidationError('New fee receiver address is required');
      return;
    }

    if (!isAddress(newFeeReceiver)) {
      setValidationError('Invalid fee receiver address format');
      return;
    }

    if (!writeFeeReceiver) {
      toast({
        title: "Error",
        description: feeReceiverError?.message || "Unable to update fee receiver",
        variant: "destructive",
      });
      return;
    }

    writeFeeReceiver();
  }, [newFeeReceiver, writeFeeReceiver, feeReceiverError, toast]);

  // Handle template update
  const handleTemplateUpdate = useCallback(() => {
    if (!newTemplateAddress) {
      setValidationError('New template address is required');
      return;
    }

    if (!isAddress(newTemplateAddress)) {
      setValidationError('Invalid template address format');
      return;
    }

    if (!writeTemplate) {
      toast({
        title: "Error",
        description: templateError?.message || "Unable to update template address",
        variant: "destructive",
      });
      return;
    }

    writeTemplate();
  }, [newTemplateAddress, writeTemplate, templateError, toast]);

  // Handle ownership transfer
  const handleOwnershipTransfer = useCallback(() => {
    if (!newOwnerAddress) {
      setValidationError('New owner address is required');
      return;
    }
    if (!writeTransferOwnership) {
      toast({
        title: "Error",
        description: transferOwnershipError?.message || "Unable to transfer ownership",
        variant: "destructive",
      });
      return;
    }
    writeTransferOwnership();
  }, [newOwnerAddress, writeTransferOwnership, transferOwnershipError, toast]);

  if (!isAdmin && !isOwnerVerified) {
    return null;
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Contract Management</CardTitle>
        <CardDescription>Manage factory contract settings</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Network Status */}
        <div className="rounded-lg border p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Network Status</h3>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${
                chain?.id === 56 ? 'bg-green-500' : 
                chain?.id === 97 ? 'bg-yellow-500' : 
                chain?.id === 204 ? 'bg-green-500' :
                chain?.id === 5611 ? 'bg-yellow-500' :
                chain?.id === 1 ? 'bg-green-500' :
                chain?.id === 11155111 ? 'bg-yellow-500' :
                chain?.id === 43114 ? 'bg-green-500' :
                chain?.id === 43113 ? 'bg-yellow-500' :
                chain?.id === 11124 ? 'bg-yellow-500' :
                chain?.id === 2741 ? 'bg-green-500' :
                chain?.id === 8453 ? 'bg-green-500' :
                chain?.id === 84532 ? 'bg-yellow-500' :
                chain?.id === 10 ? 'bg-green-500' :
                chain?.id === 11155420 ? 'bg-yellow-500' :
                chain?.id === 42161 ? 'bg-green-500' :
                chain?.id === 421614 ? 'bg-yellow-500' :
                chain?.id === 137 ? 'bg-green-500' :
                chain?.id === 80002 ? 'bg-yellow-500' :
                chain?.id === 1329 ? 'bg-green-500' :
                chain?.id === 1328 ? 'bg-yellow-500' :
                'bg-red-500'
              }`} />
              <span className="text-sm font-medium">
                {chain?.id === 56 ? 'BSC Mainnet' : 
                 chain?.id === 97 ? 'BSC Testnet' : 
                 chain?.id === 204 ? 'opBNB Mainnet' :
                 chain?.id === 5611 ? 'opBNB Testnet' :
                 chain?.id === 1 ? 'Ethereum Mainnet' :
                 chain?.id === 11155111 ? 'Sepolia Testnet' :
                 chain?.id === 43114 ? 'Avalanche Mainnet' :
                 chain?.id === 43113 ? 'Avalanche Fuji' :
                 chain?.id === 11124 ? 'Abstract Testnet' :
                 chain?.id === 2741 ? 'Abstract Mainnet' :
                 chain?.id === 8453 ? 'Base Mainnet' :
                 chain?.id === 84532 ? 'Base Sepolia' :
                 chain?.id === 10 ? 'OP Mainnet' :
                 chain?.id === 11155420 ? 'OP Sepolia' :
                 chain?.id === 42161 ? 'Arbitrum One' :
                 chain?.id === 421614 ? 'Arbitrum Sepolia' :
                 chain?.id === 137 ? 'Polygon Mainnet' :
                 chain?.id === 80002 ? 'Polygon Amoy' :
                 chain?.id === 1329 ? 'Sei Mainnet' :
                 chain?.id === 1328 ? 'Sei Atlantic' :
                 'Not Connected'}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Chain ID:</span>{' '}
              <span className="font-mono">{chain?.id || 'Unknown'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Connected Account:</span>{' '}
              <span className="font-mono">{address ? `${address.slice(0, 6)}...${address.slice(-4)}` : 'Not Connected'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Admin Status:</span>{' '}
              <span className={`font-medium ${isAdmin ? 'text-green-500' : 'text-red-500'}`}>
                {isAdmin ? 'Admin' : 'Not Admin'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Owner Status:</span>{' '}
              <span className={`font-medium ${isOwnerVerified ? 'text-green-500' : 'text-red-500'}`}>
                {isOwnerVerified ? 'Owner' : 'Not Owner'}
              </span>
            </div>
          </div>
        </div>

        {/* Contract Addresses Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Contract Addresses</h3>
          
          {/* Factory Proxy */}
          <div className="space-y-2">
            <Label>Factory Proxy</Label>
            <div className="flex items-center gap-2">
              <Input
                value={finalFactoryAddress || "Not available"}
                readOnly
                className="bg-muted font-mono text-sm"
              />
              <Button
                variant="outline"
                size="icon"
                disabled={!finalFactoryAddress || !chain?.id}
                onClick={() => {
                  const baseUrl = chain?.id === 56 ? 'bscscan.com' :
                                chain?.id === 97 ? 'testnet.bscscan.com' :
                                chain?.id === 204 ? 'opbnbscan.com' :
                                chain?.id === 5611 ? 'testnet.opbnbscan.com' :
                                chain?.id === 1 ? 'etherscan.io' :
                                chain?.id === 11155111 ? 'sepolia.etherscan.io' :
                                chain?.id === 43114 ? 'snowtrace.io' :
                                chain?.id === 43113 ? 'testnet.snowtrace.io' :
                                chain?.id === 2741 ? 'abscan.org' :
                                chain?.id === 11124 ? 'sepolia.abscan.org' :
                                chain?.id === 42161 ? 'arbiscan.io' :
                                chain?.id === 421614 ? 'sepolia.arbiscan.io' :
                                chain?.id === 10 ? 'optimistic.etherscan.io' :
                                chain?.id === 11155420 ? 'sepolia-optimism.etherscan.io' :
                                chain?.id === 137 ? 'polygonscan.com' :
                                chain?.id === 80002 ? 'amoy.polygonscan.com' :
                                chain?.id === 1329 ? 'seitrace.com' :
                                chain?.id === 1328 ? 'seitrace.com/?chain=atlantic-2' :
                                'bscscan.com'; // Default to BSC mainnet
                  if (baseUrl && finalFactoryAddress) {
                    window.open(`https://${baseUrl}/address/${finalFactoryAddress}`, '_blank');
                  }
                }}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Factory Implementation */}
          <div className="space-y-2">
            <Label>Factory Implementation</Label>
            <div className="flex items-center gap-2">
              <Input
                value={CONTRACT_ADDRESSES[chainId as keyof typeof CONTRACT_ADDRESSES]?.factoryImplementation || "Not available"}
                readOnly
                className="bg-muted font-mono text-sm"
              />
              <Button
                variant="outline"
                size="icon"
                disabled={!CONTRACT_ADDRESSES[chainId as keyof typeof CONTRACT_ADDRESSES]?.factoryImplementation || !chain?.id}
                onClick={() => {
                  const baseUrl = chain?.id === 56 ? 'bscscan.com' :
                                chain?.id === 97 ? 'testnet.bscscan.com' :
                                chain?.id === 204 ? 'opbnbscan.com' :
                                chain?.id === 5611 ? 'testnet.opbnbscan.com' :
                                chain?.id === 1 ? 'etherscan.io' :
                                chain?.id === 11155111 ? 'sepolia.etherscan.io' :
                                chain?.id === 43114 ? 'snowtrace.io' :
                                chain?.id === 43113 ? 'testnet.snowtrace.io' :
                                chain?.id === 2741 ? 'abscan.org' :
                                chain?.id === 11124 ? 'sepolia.abscan.org' :
                                chain?.id === 42161 ? 'arbiscan.io' :
                                chain?.id === 421614 ? 'sepolia.arbiscan.io' :
                                chain?.id === 10 ? 'optimistic.etherscan.io' :
                                chain?.id === 11155420 ? 'sepolia-optimism.etherscan.io' :
                                chain?.id === 137 ? 'polygonscan.com' :
                                chain?.id === 80002 ? 'amoy.polygonscan.com' :
                                chain?.id === 1329 ? 'seitrace.com' :
                                chain?.id === 1328 ? 'seitrace.com/?chain=atlantic-2' :
                                'bscscan.com'; // Default to BSC mainnet
                  const implementation = CONTRACT_ADDRESSES[chainId as keyof typeof CONTRACT_ADDRESSES]?.factoryImplementation;
                  if (baseUrl && implementation) {
                    window.open(`https://${baseUrl}/address/${implementation}`, '_blank');
                  }
                }}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Template */}
          <div className="space-y-2">
            <Label>Template</Label>
            <div className="flex items-center gap-2">
              <Input
                value={currentTemplate || "Loading..."}
                readOnly
                className="bg-muted font-mono text-sm"
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => refetchTemplate()}
                    >
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Click to refresh current template</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="outline"
                size="icon"
                disabled={!currentTemplate || !chain?.id}
                onClick={() => {
                  const baseUrl = chain?.id === 56 ? 'bscscan.com' :
                                chain?.id === 97 ? 'testnet.bscscan.com' :
                                chain?.id === 1 ? 'etherscan.io' :
                                chain?.id === 11155111 ? 'sepolia.etherscan.io' :
                                chain?.id === 43114 ? 'snowtrace.io' :
                                chain?.id === 43113 ? 'testnet.snowtrace.io' :
                                chain?.id === 2741 ? 'abscan.org' :
                                chain?.id === 11124 ? 'sepolia.abscan.org' :
                                chain?.id === 42161 ? 'arbiscan.io' :
                                chain?.id === 421614 ? 'sepolia.arbiscan.io' :
                                chain?.id === 10 ? 'optimistic.etherscan.io' :
                                chain?.id === 11155420 ? 'sepolia-optimism.etherscan.io' :
                                chain?.id === 137 ? 'polygonscan.com' :
                                chain?.id === 80002 ? 'amoy.polygonscan.com' :
                                chain?.id === 1329 ? 'seitrace.com' :
                                chain?.id === 1328 ? 'seitrace.com/?chain=atlantic-2' :
                                'bscscan.com'; // Default to BSC mainnet
                  if (baseUrl && currentTemplate) {
                    window.open(`https://${baseUrl}/address/${currentTemplate}`, '_blank');
                  }
                }}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Platform Fee Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Platform Fee</h3>
          
          {/* Current Fee */}
          <div className="space-y-2">
            <Label>Current Fee</Label>
            <div className="flex items-center gap-2">
              <Input
                value={currentFee ? formatEther(currentFee) : "Loading..."}
                readOnly
                className="bg-muted font-mono text-sm"
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => refetchFee()}
                    >
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Click to refresh current fee</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* New Fee */}
          <div className="space-y-2">
            <Label>New Fee ({getNativeCurrency()})</Label>
            <div className="flex items-center gap-2">
              <Input
                placeholder={`Enter new platform fee in ${getNativeCurrency()}`}
                value={newFee}
                onChange={(e) => setNewFee(e.target.value)}
                className="font-mono text-sm"
              />
              <Button 
                onClick={handleFeeUpdate}
                disabled={isSettingFee}
              >
                {isSettingFee ? "Updating..." : "Update"}
              </Button>
            </div>
          </div>
        </div>

        {/* Fee Receiver Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Fee Receiver</h3>
          
          {/* Current Receiver */}
          <div className="space-y-2">
            <Label>Current Receiver</Label>
            <div className="flex items-center gap-2">
              <Input
                value={currentFeeReceiver || "Loading..."}
                readOnly
                className="bg-muted font-mono text-sm"
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => refetchReceiver()}
                    >
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Click to refresh current receiver</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="outline"
                size="icon"
                disabled={!currentFeeReceiver || !chain?.id}
                onClick={() => {
                  const baseUrl = chain?.id === 56 ? 'bscscan.com' :
                                chain?.id === 97 ? 'testnet.bscscan.com' :
                                chain?.id === 204 ? 'opbnbscan.com' :
                                chain?.id === 5611 ? 'testnet.opbnbscan.com' :
                                chain?.id === 1 ? 'etherscan.io' :
                                chain?.id === 11155111 ? 'sepolia.etherscan.io' :
                                chain?.id === 43114 ? 'snowtrace.io' :
                                chain?.id === 43113 ? 'testnet.snowtrace.io' :
                                chain?.id === 2741 ? 'abscan.org' :
                                chain?.id === 11124 ? 'sepolia.abscan.org' :
                                chain?.id === 42161 ? 'arbiscan.io' :
                                chain?.id === 421614 ? 'sepolia.arbiscan.io' :
                                chain?.id === 10 ? 'optimistic.etherscan.io' :
                                chain?.id === 11155420 ? 'sepolia-optimism.etherscan.io' :
                                chain?.id === 137 ? 'polygonscan.com' :
                                chain?.id === 80002 ? 'amoy.polygonscan.com' :
                                chain?.id === 1329 ? 'seitrace.com' :
                                chain?.id === 1328 ? 'seitrace.com/?chain=atlantic-2' :
                                'bscscan.com';
                  if (baseUrl && currentFeeReceiver) {
                    window.open(`https://${baseUrl}/address/${currentFeeReceiver}`, '_blank');
                  }
                }}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* New Receiver */}
          <div className="space-y-2">
            <Label>New Receiver</Label>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Enter new receiver address"
                value={newFeeReceiver}
                onChange={(e) => setNewFeeReceiver(e.target.value)}
              />
              <Button 
                onClick={handleReceiverUpdate}
                disabled={isSettingReceiver}
              >
                {isSettingReceiver ? "Updating..." : "Update"}
              </Button>
            </div>
          </div>
        </div>

        {/* Template Management */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Template Management</h3>
          
          {/* Current Template */}
          <div className="space-y-2">
            <Label>Current Template</Label>
            <div className="flex items-center gap-2">
              <Input
                value={currentTemplate || "Loading..."}
                readOnly
                className="bg-muted font-mono text-sm"
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => refetchTemplate()}
                    >
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Click to refresh current template</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="outline"
                size="icon"
                disabled={!currentTemplate || !chain?.id}
                onClick={() => {
                  const baseUrl = chain?.id === 56 ? 'bscscan.com' :
                                chain?.id === 97 ? 'testnet.bscscan.com' :
                                chain?.id === 204 ? 'opbnbscan.com' :
                                chain?.id === 5611 ? 'testnet.opbnbscan.com' :
                                chain?.id === 1 ? 'etherscan.io' :
                                chain?.id === 11155111 ? 'sepolia.etherscan.io' :
                                chain?.id === 43114 ? 'snowtrace.io' :
                                chain?.id === 43113 ? 'testnet.snowtrace.io' :
                                chain?.id === 2741 ? 'abscan.org' :
                                chain?.id === 11124 ? 'sepolia.abscan.org' :
                                chain?.id === 42161 ? 'arbiscan.io' :
                                chain?.id === 421614 ? 'sepolia.arbiscan.io' :
                                chain?.id === 10 ? 'optimistic.etherscan.io' :
                                chain?.id === 11155420 ? 'sepolia-optimism.etherscan.io' :
                                chain?.id === 137 ? 'polygonscan.com' :
                                chain?.id === 80002 ? 'amoy.polygonscan.com' :
                                chain?.id === 1329 ? 'seitrace.com' :
                                chain?.id === 1328 ? 'seitrace.com/?chain=atlantic-2' :
                                'bscscan.com'; // Default to BSC mainnet
                  if (baseUrl && currentTemplate) {
                    window.open(`https://${baseUrl}/address/${currentTemplate}`, '_blank');
                  }
                }}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* New Template */}
          {isOwnerVerified && (
            <div className="space-y-2">
              <Label>New Template Address</Label>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Enter new template address"
                  value={newTemplateAddress}
                  onChange={(e) => setNewTemplateAddress(e.target.value)}
                />
                <Button 
                  onClick={handleTemplateUpdate}
                  disabled={isSettingTemplate || !newTemplateAddress}
                >
                  {isSettingTemplate ? "Updating..." : "Update"}
                </Button>
              </div>
              {validationError && (
                <p className="text-sm text-destructive">{validationError}</p>
              )}
              <p className="text-sm text-muted-foreground">
                Make sure the new template address is a valid V8 token implementation contract.
              </p>
            </div>
          )}
        </div>

        {/* Ownership Management */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Ownership Management</h3>
          
          {/* Current Owner */}
          <div className="space-y-2">
            <Label>Current Owner</Label>
            <div className="flex items-center gap-2">
              <Input
                value={contractOwner || "Loading..."}
                readOnly
                className="bg-muted font-mono text-sm"
              />
              <Button
                variant="outline"
                size="icon"
                disabled={!contractOwner || !chain?.id}
                onClick={() => {
                  const baseUrl = chain?.id === 56 ? 'bscscan.com' :
                                chain?.id === 97 ? 'testnet.bscscan.com' :
                                chain?.id === 204 ? 'opbnbscan.com' :
                                chain?.id === 5611 ? 'testnet.opbnbscan.com' :
                                chain?.id === 1 ? 'etherscan.io' :
                                chain?.id === 11155111 ? 'sepolia.etherscan.io' :
                                chain?.id === 43114 ? 'snowtrace.io' :
                                chain?.id === 43113 ? 'testnet.snowtrace.io' :
                                chain?.id === 2741 ? 'abscan.org' :
                                chain?.id === 11124 ? 'sepolia.abscan.org' :
                                chain?.id === 42161 ? 'arbiscan.io' :
                                chain?.id === 421614 ? 'sepolia.arbiscan.io' :
                                chain?.id === 10 ? 'optimistic.etherscan.io' :
                                chain?.id === 11155420 ? 'sepolia-optimism.etherscan.io' :
                                chain?.id === 137 ? 'polygonscan.com' :
                                chain?.id === 80002 ? 'amoy.polygonscan.com' :
                                chain?.id === 1329 ? 'seitrace.com' :
                                chain?.id === 1328 ? 'seitrace.com/?chain=atlantic-2' :
                                'bscscan.com';
                  if (baseUrl && contractOwner) {
                    window.open(`https://${baseUrl}/address/${contractOwner}`, '_blank');
                  }
                }}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Transfer Ownership */}
          {isOwnerVerified && (
            <div className="space-y-2">
              <Label>Transfer Ownership</Label>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Enter new owner address"
                  value={newOwnerAddress}
                  onChange={(e) => setNewOwnerAddress(e.target.value)}
                />
                <Button 
                  onClick={handleOwnershipTransfer}
                  disabled={isTransferringOwnership || !newOwnerAddress}
                >
                  {isTransferringOwnership ? "Transferring..." : "Transfer"}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Warning: Transferring ownership will remove your ability to manage this contract. Make sure the new owner address is correct.
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
